import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import Pagination from "@/components/core/pagination";
import { Content } from "@/components/layouts/content";
import { CreateWebhookEndpointModal } from "@/components/webhooks/create-webhook-endpoint-modal";
import { EditWebhookEndpointModal } from "@/components/webhooks/edit-webhook-endpoint-modal";
import { useWebhookEndpointDeleteMutation, webhookEndpointsQueryOptions } from "@/features/dashboard/api/queries/webhook-endpoint-query";
import type { WebhookEndpoint } from "@/features/dashboard/types/webhook-endpoint";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

const columns: ColumnDef<WebhookEndpoint>[] = [
	{
		id: "url",
		label: "Endpoint",
		cellClassName: "min-w-[300px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => (
			<div className="flex flex-col space-y-1">
				<span className="font-medium font-mono text-sm text-blue-600 break-all">{data.url}</span>
				{data.description && <span className="text-sm text-gray-500">{data.description}</span>}
			</div>
		),
	},
	{
		id: "events",
		label: "Events",
		cellClassName: "min-w-[200px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => (
			<div className="flex flex-wrap gap-1">
				{data.events?.map((event) => (
					<span key={event} className="px-2 py-0.5 rounded-full text-xs font-mono bg-blue-50 text-blue-700">
						{event}
					</span>
				))}
			</div>
		),
	},
	{
		id: "is_active",
		label: "Status",
		cellClassName: "min-w-[80px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => (
			<span className={`px-2 py-1 rounded-full text-xs font-medium ${data.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}>
				{data.is_active ? "Active" : "Inactive"}
			</span>
		),
	},
	{
		id: "webhook_calls_count",
		label: "Deliveries",
		cellClassName: "min-w-[80px] text-center",
		headerClassName: "whitespace-nowrap text-center",
		cell: ({ data }) => <span className="font-medium">{data.webhook_calls_count ?? 0}</span>,
	},
	{
		id: "created_at",
		label: "Created",
		cellClassName: "min-w-[120px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-900">{data.created_at ? new Date(data.created_at).toLocaleDateString() : "-"}</span>,
	},
];

export const Route = createFileRoute("/_dashboard/webhooks")({
	validateSearch: z.object({
		"filter[search]": z.string().optional(),
		per_page: z.number().optional(),
		page: z.number().nullable().optional(),
	}),
	component: Page,
});

function Page() {
	const navigate = useNavigate();
	const search = Route.useSearch();
	const [editingEndpoint, setEditingEndpoint] = useState<WebhookEndpoint | null>(null);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState(search["filter[search]"] || "");
	const deleteEndpointMutation = useWebhookEndpointDeleteMutation();

	const endpointsQuery = useQuery(webhookEndpointsQueryOptions({ ...search }));

	const handleEdit = (endpoint: WebhookEndpoint) => {
		setEditingEndpoint(endpoint);
		setIsEditModalOpen(true);
	};

	const handleDelete = async (endpoint: WebhookEndpoint) => {
		if (endpoint.id) {
			await deleteEndpointMutation.mutateAsync(endpoint.id);
			toast.success("Webhook endpoint deleted");
		}
	};

	const handleSearch = async () => {
		await navigate({
			to: "/webhooks",
			search: (old) => ({
				...old,
				"filter[search]": searchTerm || undefined,
				page: 1,
			}),
		});
	};

	const handleSearchKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") handleSearch();
	};

	const clearSearch = async () => {
		setSearchTerm("");
		await navigate({
			to: "/webhooks",
			search: (old) => ({
				...old,
				"filter[search]": undefined,
				page: 1,
			}),
		});
	};

	const endpoints = endpointsQuery.data?.data || [];
	const meta = endpointsQuery.data?.meta || { current_page: 1, last_page: 1, from: 1, to: 0, total: 0, per_page: 15, status_count: {} };

	if (endpointsQuery.isLoading) {
		return (
			<Content pageName="Webhooks">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
						<p className="mt-2 text-sm text-gray-600">Loading webhooks...</p>
					</div>
				</div>
			</Content>
		);
	}

	return (
		<Content pageName="Webhooks">
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="inline-flex flex-col w-auto">
						<span className="text-xl text-foreground-dark font-semibold">Webhook Endpoints</span>
						<span className="text-base text-foreground/70">Manage outbound webhook destinations</span>
					</div>
					<Button onClick={() => setIsCreateModalOpen(true)}>
						<IconPlus size={18} stroke={2} />
						<span>Add Endpoint</span>
					</Button>
				</div>
				<div className="flex flex-col w-full gap-4">
					<div className="flex items-center gap-2 w-full max-w-md">
						<div className="relative flex-1">
							<IconSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
							<input
								type="text"
								placeholder="Search endpoints..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								onKeyPress={handleSearchKeyPress}
								className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							/>
							{searchTerm && (
								<button type="button" onClick={clearSearch} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
									x
								</button>
							)}
						</div>
						<Button onClick={handleSearch} variant="outline">
							Search
						</Button>
					</div>
					<DataTable
						className="border-collapse"
						data={endpoints}
						columns={columns}
						stickyHead={false}
						stickyCol={false}
						showBorder={true}
						compact={true}
						showToolbar={false}
						onEdit={handleEdit}
						onDelete={handleDelete}
					/>
					<Pagination
						onClick={async (page) => {
							await navigate({
								to: "/webhooks",
								search: (old) => ({ ...old, page }),
							});
						}}
						initialPage={meta.current_page}
						pageCount={meta.last_page}
					/>
				</div>
			</div>
			<CreateWebhookEndpointModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSuccess={() => endpointsQuery.refetch()} />
			{editingEndpoint && (
				<EditWebhookEndpointModal
					endpoint={editingEndpoint}
					isOpen={isEditModalOpen}
					onClose={() => {
						setIsEditModalOpen(false);
						setEditingEndpoint(null);
					}}
					onSuccess={() => endpointsQuery.refetch()}
				/>
			)}
		</Content>
	);
}
